import { lazy, useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import OrderFormContainer from '@components/RequisitionCreate/OrderFormContainer/OrderFormContainer';
import { Sanitize } from '@components/Sanitize';
import { DRAFT_ORDERS, PATIENTS } from '@constants/routes';
import useDraftOrderDetail from '@contexts/useDraftOrderDetail';
import { getDraftOrderDetail } from '@store/actions';

const RequisitionJSONSchemaForm = lazy(
  () => import('@components/RequisitionCreate/RequisitionJSONSchemaForm/RequisitionJSONSchemaForm'),
);

export default function DraftOrderDetail() {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const params = useParams();
  const draftOrderId = useMemo(() => params?.id, [params]);
  const { draftOrder, setDraftOrder, loading, setLoading } = useDraftOrderDetail();
  const [loadFailed, setLoadFailed] = useState(false);

  const prevPath = locationState?.prevPath as string | undefined;
  const cancelReturnsToPatient = Boolean(prevPath?.startsWith(PATIENTS));

  const onCancel = useCallback(() => {
    navigate(prevPath ?? DRAFT_ORDERS);
  }, [navigate, prevPath]);

  const moveToDraftOrders = useCallback(() => {
    navigate(DRAFT_ORDERS);
  }, [navigate]);

  const loadData = useCallback(async () => {
    if (!draftOrderId) return;

    try {
      setLoadFailed(false);
      setLoading(true);

      const res = await getDraftOrderDetail(draftOrderId);

      setDraftOrder({ ...res });
    } catch (err) {
      console.error('Error on getting a draft order detail: ', err);
      setDraftOrder(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [draftOrderId, setDraftOrder, setLoading]);

  const breadcrumbs = useMemo(
    () => [
      {
        onClick: moveToDraftOrders,
        children: 'Draft Orders',
      },
      {
        active: true,
        children: <Sanitize>{draftOrder?.name ?? ''}</Sanitize>,
      },
    ],
    [moveToDraftOrders, draftOrder],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!loadFailed || loading) {
      return;
    }

    if (prevPath?.startsWith(PATIENTS)) {
      navigate(prevPath, { replace: true });
      return;
    }

    navigate(DRAFT_ORDERS, { replace: true });
  }, [loadFailed, loading, navigate, prevPath]);

  if (loading || loadFailed) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <LoadingIndicator />
      </div>
    );
  }

  if (!draftOrder) {
    return null;
  }

  const rerequisitionFrom = draftOrder.rerequisition_from ?? draftOrder.formdata.rerequisition;
  const rerequisitionType = draftOrder.rerequisition_type ?? draftOrder.formdata.rerequisition_type;

  return (
    <OrderFormContainer
      breadcrumbs={breadcrumbs}
      title='Your Draft Order'
      onCancel={onCancel}
      cancelReturnsToPatient={cancelReturnsToPatient}
      rerequisitionFrom={rerequisitionFrom}
      rerequisitionType={rerequisitionFrom ? rerequisitionType : undefined}
    >
      <RequisitionJSONSchemaForm
        draftFormData={draftOrder.formdata}
        initialProductIds={draftOrder.formdata.product_ids}
        selectedCampaignCode={draftOrder.formdata.campaign_code}
        selectedRequisitionId={rerequisitionFrom}
        rerequisitionType={rerequisitionFrom ? rerequisitionType : undefined}
      />
    </OrderFormContainer>
  );
}
